import { ErrorHandler, Injectable, inject } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * After a deploy the old build's lazy chunks are gone (nginx serves only the current build).
 * A tab opened before the deploy then fails to load a not-yet-visited area (forum, premium …)
 * and nothing happens. Fix: reload once, straight to the page the user wanted.
 * A second failure within RELOAD_GUARD_MS is not retried (no reload loop when a chunk is
 * really broken).
 */
const STORAGE_KEY = 'chunkReloadAt';
export const RELOAD_GUARD_MS = 30_000;

/** webpack ChunkLoadError / failed dynamic import (also when nginx answers with index.html) */
export function isChunkLoadError(error: unknown): boolean {
  const e = (error as any)?.rejection ?? error; // unhandled promise rejections are wrapped
  const text = `${e?.name ?? ''} ${e?.message ?? e ?? ''}`;
  return /ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
    text
  );
}

/**
 * reloads the page once (to `url` if given)
 * @returns {boolean} true if a reload was started, false if the guard blocked it
 */
export function reloadForNewBuild(
  url?: string,
  storage: Pick<Storage, 'getItem' | 'setItem'> | null = safeSessionStorage(),
  location: Pick<Location, 'assign' | 'reload'> = window.location,
  now = Date.now()
): boolean {
  const last = Number(storage?.getItem(STORAGE_KEY) || 0);
  if (last && now - last < RELOAD_GUARD_MS) return false;
  try {
    storage?.setItem(STORAGE_KEY, String(now));
  } catch {
    // private mode: reload anyway; worst case one extra reload
  }
  if (url) location.assign(url);
  else location.reload();
  return true;
}

function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** global errors (dynamic imports outside routing): reload on chunk errors, else default handling */
@Injectable()
export class ChunkReloadErrorHandler extends ErrorHandler {
  override handleError(error: unknown): void {
    if (isChunkLoadError(error) && reloadForNewBuild()) return;
    super.handleError(error);
  }
}

/** navigation into a lazy route whose chunk is gone: reload straight to the target URL */
@Injectable({ providedIn: 'root' })
export class ChunkReloadNavigationService {
  private readonly router = inject(Router);

  start(): void {
    this.router.events
      .pipe(filter((e): e is NavigationError => e instanceof NavigationError))
      .subscribe((e) => {
        if (isChunkLoadError(e.error)) reloadForNewBuild(e.url);
      });
  }
}
